import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthStore } from '../stores/authStore';
import api from '../lib/api';
import socketManager from '../lib/socket';
import {
  Zap, TrendingUp, TrendingDown, Activity, DollarSign,
  Play, Square, Settings, BarChart3, Clock, Target,
  AlertTriangle, CheckCircle, XCircle, Wifi, WifiOff,
  ArrowUpCircle, ArrowDownCircle, Flame, RefreshCw
} from 'lucide-react';

const COINS = {
  BTC: { name: 'Bitcoin', symbol: 'BTC', color: '#F7931A', icon: '₿' },
  ETH: { name: 'Ethereum', symbol: 'ETH', color: '#627EEA', icon: 'Ξ' },
  SOL: { name: 'Solana', symbol: 'SOL', color: '#9945FF', icon: '◎' },
  XRP: { name: 'XRP', symbol: 'XRP', color: '#00AAE4', icon: '✕' }
};

function formatPrice(price, coin) {
  if (!price) return '--';
  if (coin === 'BTC') return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (coin === 'ETH') return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `$${price.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
}

function formatPnl(pnl) {
  if (!pnl && pnl !== 0) return '--';
  const sign = pnl >= 0 ? '+' : '';
  return `${sign}$${pnl.toFixed(2)}`;
}

function TimeRemaining({ endTime }) {
  const [remaining, setRemaining] = useState('');
  
  useEffect(() => {
    const update = () => {
      if (!endTime) { setRemaining('--'); return; }
      const diff = endTime - Date.now();
      if (diff <= 0) { setRemaining('RESOLVED'); return; }
      const min = Math.floor(diff / 60000);
      const sec = Math.floor((diff % 60000) / 1000);
      setRemaining(`${min}:${sec.toString().padStart(2, '0')}`);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [endTime]);
  
  return <span className="font-mono">{remaining}</span>;
}

export default function DegenMode() {
  const { user } = useAuthStore();
  const [status, setStatus] = useState(null);
  const [prices, setPrices] = useState({});
  const [trades, setTrades] = useState([]);
  const [stats, setStats] = useState({ trades: 0, wins: 0, losses: 0, pnl: 0, winRate: 0 });
  const [predictions, setPredictions] = useState({});
  const [config, setConfig] = useState({
    enabled: false,
    mode: 'paper',
    coins: ['BTC', 'ETH', 'SOL', 'XRP'],
    betAmount: 2,
    strongBetAmount: 3,
    maxDailyLoss: 20,
    confidenceThreshold: 58
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const pollRef = useRef(null);

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      const [statusRes, pricesRes, tradesRes, statsRes, predsRes, configRes] = await Promise.allSettled([
        api.get('/degen/status'),
        api.get('/degen/prices'),
        api.get('/degen/trades'),
        api.get('/degen/stats'),
        api.get('/degen/predictions'),
        api.get('/degen/config')
      ]);
      
      if (statusRes.status === 'fulfilled') setStatus(statusRes.value.data);
      if (pricesRes.status === 'fulfilled') setPrices(pricesRes.value.data);
      if (tradesRes.status === 'fulfilled') setTrades(tradesRes.value.data || []);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
      if (predsRes.status === 'fulfilled') setPredictions(predsRes.value.data || {});
      if (configRes.status === 'fulfilled') {
        const cfg = configRes.value.data;
        setConfig(prev => ({
          ...prev,
          ...cfg,
          confidenceThreshold: Math.round((cfg.confidenceThreshold || 0.58) * 100)
        }));
      }
    } catch (e) {
      console.error('Degen fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    pollRef.current = setInterval(fetchData, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchData]);

  // Socket subscriptions
  useEffect(() => {
    if (!user?._id) return;
    
    socketManager.emit('subscribe:degen', user._id);
    
    const onTrade = (trade) => {
      setTrades(prev => [trade, ...prev].slice(0, 100));
    };
    const onResolved = (trade) => {
      setTrades(prev => prev.map(t => t.timestamp === trade.timestamp ? trade : t));
      fetchData(); // Refresh stats
    };
    const onPrediction = (pred) => {
      setPredictions(prev => ({ ...prev, [`${pred.coin}-${pred.timestamp}`]: pred }));
    };
    
    socketManager.on('degenTrade', onTrade);
    socketManager.on('degenTradeResolved', onResolved);
    socketManager.on('degenPrediction', onPrediction);
    
    return () => {
      socketManager.emit('unsubscribe:degen', user._id);
      socketManager.off('degenTrade', onTrade);
      socketManager.off('degenTradeResolved', onResolved);
      socketManager.off('degenPrediction', onPrediction);
    };
  }, [user?._id, fetchData]);

  // Toggle engine on/off
  const toggleEngine = async () => {
    try {
      setSaving(true);
      if (config.enabled) {
        await api.post('/degen/stop');
        setConfig(prev => ({ ...prev, enabled: false }));
      } else {
        await api.post('/degen/start', {
          mode: config.mode,
          coins: config.coins,
          betAmount: config.betAmount,
          strongBetAmount: config.strongBetAmount,
          confidenceThreshold: config.confidenceThreshold / 100
        });
        setConfig(prev => ({ ...prev, enabled: true }));
      }
    } catch (e) {
      console.error('Toggle error:', e);
    } finally {
      setSaving(false);
    }
  };

  // Save config
  const saveConfig = async () => {
    try {
      setSaving(true);
      await api.put('/degen/config', {
        ...config,
        confidenceThreshold: config.confidenceThreshold / 100
      });
    } catch (e) {
      console.error('Save config error:', e);
    } finally {
      setSaving(false);
    }
  };

  // Toggle coin
  const toggleCoin = (coin) => {
    setConfig(prev => {
      const coins = prev.coins.includes(coin)
        ? prev.coins.filter(c => c !== coin)
        : [...prev.coins, coin];
      return { ...prev, coins };
    });
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500" />
      </div>
    );
  }

  const wsConnected = status?.wsConnected;
  const activeMarkets = status?.markets || [];

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl shadow-lg shadow-orange-500/25">
            <Flame className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              DEGEN MODE
              <span className="text-xs px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded-full font-semibold">5-MIN CRYPTO</span>
            </h1>
            <p className="text-sm text-dark-400">
              Predict crypto direction in 5-minute windows • Powered by Binance real-time feeds
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {/* WS Status */}
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${
            wsConnected ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
          }`}>
            {wsConnected ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
            {wsConnected ? 'LIVE' : 'OFFLINE'}
          </div>
          
          {/* Settings toggle */}
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="p-2 rounded-lg bg-dark-700/50 hover:bg-dark-700 transition-colors"
          >
            <Settings className="w-5 h-5 text-dark-400" />
          </button>
          
          {/* Start/Stop */}
          <button
            onClick={toggleEngine}
            disabled={saving}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${
              config.enabled
                ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30'
                : 'bg-gradient-to-r from-orange-500 to-red-600 text-white hover:shadow-lg hover:shadow-orange-500/25'
            }`}
          >
            {config.enabled ? <Square className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            {saving ? 'Saving...' : config.enabled ? 'STOP' : 'START'}
          </button>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="bg-dark-800/60 backdrop-blur-sm border border-dark-700/50 rounded-2xl p-6 space-y-5">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Settings className="w-5 h-5 text-orange-400" /> Configuration
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Mode */}
            <div>
              <label className="text-xs text-dark-400 font-medium uppercase tracking-wider">Mode</label>
              <select
                value={config.mode}
                onChange={(e) => setConfig(prev => ({ ...prev, mode: e.target.value }))}
                className="mt-1 w-full bg-dark-700/50 border border-dark-600/50 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="paper">Paper (Simulated)</option>
                <option value="real">Real (Live Trading)</option>
              </select>
            </div>
            
            {/* Bet Amount */}
            <div>
              <label className="text-xs text-dark-400 font-medium uppercase tracking-wider">Bet Amount ($)</label>
              <input
                type="number"
                min="1"
                max="100"
                step="0.5"
                value={config.betAmount}
                onChange={(e) => setConfig(prev => ({ ...prev, betAmount: parseFloat(e.target.value) || 2 }))}
                className="mt-1 w-full bg-dark-700/50 border border-dark-600/50 rounded-lg px-3 py-2 text-white text-sm"
              />
            </div>
            
            {/* Strong Bet Amount */}
            <div>
              <label className="text-xs text-dark-400 font-medium uppercase tracking-wider">Strong Bet ($)</label>
              <input
                type="number"
                min="1"
                max="200"
                step="0.5"
                value={config.strongBetAmount}
                onChange={(e) => setConfig(prev => ({ ...prev, strongBetAmount: parseFloat(e.target.value) || 3 }))}
                className="mt-1 w-full bg-dark-700/50 border border-dark-600/50 rounded-lg px-3 py-2 text-white text-sm"
              />
            </div>
            
            {/* Confidence Threshold */}
            <div>
              <label className="text-xs text-dark-400 font-medium uppercase tracking-wider">Min Confidence (%)</label>
              <input
                type="number"
                min="52"
                max="80"
                value={config.confidenceThreshold}
                onChange={(e) => setConfig(prev => ({ ...prev, confidenceThreshold: parseInt(e.target.value) || 58 }))}
                className="mt-1 w-full bg-dark-700/50 border border-dark-600/50 rounded-lg px-3 py-2 text-white text-sm"
              />
            </div>
          </div>
          
          {/* Coins */}
          <div>
            <label className="text-xs text-dark-400 font-medium uppercase tracking-wider mb-2 block">Active Coins</label>
            <div className="flex gap-3">
              {Object.entries(COINS).map(([key, coin]) => (
                <button
                  key={key}
                  onClick={() => toggleCoin(key)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all border ${
                    config.coins.includes(key)
                      ? 'border-orange-500/50 bg-orange-500/10 text-orange-400'
                      : 'border-dark-600/50 bg-dark-700/30 text-dark-400 hover:border-dark-500'
                  }`}
                >
                  <span style={{ color: coin.color }}>{coin.icon}</span>
                  {coin.symbol}
                </button>
              ))}
            </div>
          </div>
          
          <button
            onClick={saveConfig}
            disabled={saving}
            className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-semibold text-sm transition-colors"
          >
            {saving ? 'Saving...' : 'Save Config'}
          </button>
        </div>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Trades Today" value={stats.trades} icon={<BarChart3 className="w-4 h-4" />} />
        <StatCard label="Wins" value={stats.wins} icon={<CheckCircle className="w-4 h-4 text-emerald-400" />} color="emerald" />
        <StatCard label="Losses" value={stats.losses} icon={<XCircle className="w-4 h-4 text-red-400" />} color="red" />
        <StatCard label="Win Rate" value={`${stats.winRate?.toFixed(1) || 0}%`} icon={<Target className="w-4 h-4 text-orange-400" />} color="orange" />
        <StatCard 
          label="P&L Today" 
          value={formatPnl(stats.pnl)} 
          icon={<DollarSign className="w-4 h-4" />} 
          color={stats.pnl >= 0 ? 'emerald' : 'red'} 
        />
      </div>

      {/* Live Prices + Active Markets */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Live Prices */}
        <div className="bg-dark-800/60 backdrop-blur-sm border border-dark-700/50 rounded-2xl p-5">
          <h3 className="text-sm font-semibold text-dark-300 uppercase tracking-wider mb-4 flex items-center gap-2">
            <Activity className="w-4 h-4 text-orange-400" /> Live Prices (Binance)
          </h3>
          <div className="space-y-3">
            {Object.entries(COINS).map(([key, coin]) => {
              const priceData = prices[key];
              const change = priceData?.priceChange24h;
              const isUp = change > 0;
              
              return (
                <div key={key} className="flex items-center justify-between p-3 bg-dark-700/30 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg font-bold" style={{ backgroundColor: `${coin.color}20`, color: coin.color }}>
                      {coin.icon}
                    </div>
                    <div>
                      <p className="text-white font-semibold">{coin.name}</p>
                      <p className="text-xs text-dark-400">{coin.symbol}/USDT</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-mono font-semibold">
                      {formatPrice(priceData?.price, key)}
                    </p>
                    {change !== undefined && (
                      <p className={`text-xs font-medium flex items-center justify-end gap-1 ${isUp ? 'text-emerald-400' : 'text-red-400'}`}>
                        {isUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        {isUp ? '+' : ''}{change?.toFixed(2)}%
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Active Markets */}
        <div className="bg-dark-800/60 backdrop-blur-sm border border-dark-700/50 rounded-2xl p-5">
          <h3 className="text-sm font-semibold text-dark-300 uppercase tracking-wider mb-4 flex items-center gap-2">
            <Clock className="w-4 h-4 text-orange-400" /> Active 5-Min Markets
          </h3>
          {activeMarkets.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-dark-500">
              <Clock className="w-8 h-8 mb-2 opacity-50" />
              <p className="text-sm">No active 5-min markets right now</p>
              <p className="text-xs mt-1">Markets run during trading hours</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activeMarkets.map((market, i) => {
                const coinInfo = COINS[market.coin];
                const currentPrice = prices[market.coin]?.price;
                const delta = currentPrice && market.priceToBeat
                  ? ((currentPrice - market.priceToBeat) / market.priceToBeat * 100)
                  : null;
                
                return (
                  <div key={i} className="p-3 bg-dark-700/30 rounded-xl border border-dark-600/30">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span style={{ color: coinInfo?.color }} className="font-bold">{coinInfo?.icon}</span>
                        <span className="text-white font-medium text-sm">{market.coin} 5-Min</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-3.5 h-3.5 text-dark-400" />
                        <TimeRemaining endTime={market.endTime} />
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-dark-400">
                        Beat: <span className="text-white font-mono">{market.priceToBeat ? `$${market.priceToBeat.toLocaleString()}` : '?'}</span>
                      </span>
                      {delta !== null && (
                        <span className={`font-semibold ${delta > 0 ? 'text-emerald-400' : delta < 0 ? 'text-red-400' : 'text-dark-400'}`}>
                          {delta > 0 ? '▲' : delta < 0 ? '▼' : '—'} {Math.abs(delta).toFixed(3)}%
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Recent Trades */}
      <div className="bg-dark-800/60 backdrop-blur-sm border border-dark-700/50 rounded-2xl p-5">
        <h3 className="text-sm font-semibold text-dark-300 uppercase tracking-wider mb-4 flex items-center gap-2">
          <Zap className="w-4 h-4 text-orange-400" /> Recent Trades
        </h3>
        
        {trades.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-dark-500">
            <Flame className="w-10 h-10 mb-3 opacity-30" />
            <p className="text-sm font-medium">No trades yet</p>
            <p className="text-xs mt-1">{config.enabled ? 'Waiting for next 5-min market window...' : 'Enable Degen Mode to start trading'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-dark-400 text-xs uppercase tracking-wider border-b border-dark-700/50">
                  <th className="text-left pb-3 pl-2">Coin</th>
                  <th className="text-left pb-3">Direction</th>
                  <th className="text-right pb-3">Amount</th>
                  <th className="text-right pb-3">Confidence</th>
                  <th className="text-right pb-3">Result</th>
                  <th className="text-right pb-3">P&L</th>
                  <th className="text-right pb-3 pr-2">Time</th>
                </tr>
              </thead>
              <tbody>
                {trades.slice(0, 30).map((trade, i) => {
                  const coinInfo = COINS[trade.coin];
                  return (
                    <tr key={i} className="border-b border-dark-700/30 hover:bg-dark-700/20 transition-colors">
                      <td className="py-3 pl-2">
                        <div className="flex items-center gap-2">
                          <span style={{ color: coinInfo?.color }}>{coinInfo?.icon}</span>
                          <span className="text-white font-medium">{trade.coin}</span>
                          {trade.mode === 'paper' && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-dark-600/50 text-dark-400 rounded">PAPER</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3">
                        <span className={`flex items-center gap-1 font-semibold ${
                          trade.direction === 'up' ? 'text-emerald-400' : 'text-red-400'
                        }`}>
                          {trade.direction === 'up' ? <ArrowUpCircle className="w-4 h-4" /> : <ArrowDownCircle className="w-4 h-4" />}
                          {trade.direction?.toUpperCase()}
                        </span>
                      </td>
                      <td className="py-3 text-right text-white font-mono">${trade.amount?.toFixed(2)}</td>
                      <td className="py-3 text-right">
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                          trade.confidence >= 0.68 ? 'bg-emerald-500/10 text-emerald-400' :
                          trade.confidence >= 0.58 ? 'bg-orange-500/10 text-orange-400' :
                          'bg-dark-600/50 text-dark-400'
                        }`}>
                          {(trade.confidence * 100).toFixed(0)}%
                        </span>
                      </td>
                      <td className="py-3 text-right">
                        {trade.resolved ? (
                          trade.won ? (
                            <span className="text-emerald-400 font-semibold flex items-center justify-end gap-1">
                              <CheckCircle className="w-3.5 h-3.5" /> WIN
                            </span>
                          ) : (
                            <span className="text-red-400 font-semibold flex items-center justify-end gap-1">
                              <XCircle className="w-3.5 h-3.5" /> LOSS
                            </span>
                          )
                        ) : (
                          <span className="text-dark-400 text-xs flex items-center justify-end gap-1">
                            <RefreshCw className="w-3 h-3 animate-spin" /> Pending
                          </span>
                        )}
                      </td>
                      <td className={`py-3 text-right font-mono font-semibold ${
                        trade.pnl > 0 ? 'text-emerald-400' : trade.pnl < 0 ? 'text-red-400' : 'text-dark-400'
                      }`}>
                        {trade.resolved ? formatPnl(trade.pnl) : '--'}
                      </td>
                      <td className="py-3 text-right text-dark-400 text-xs pr-2">
                        {trade.timestamp ? new Date(trade.timestamp).toLocaleTimeString() : '--'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Algorithm Info */}
      <div className="bg-dark-800/60 backdrop-blur-sm border border-dark-700/50 rounded-2xl p-5">
        <h3 className="text-sm font-semibold text-dark-300 uppercase tracking-wider mb-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-orange-400" /> How It Works
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-dark-400">
          <div className="space-y-1">
            <p className="text-white font-semibold text-sm">📡 Real-Time Data</p>
            <p>Binance WebSocket feeds for BTC, ETH, SOL, XRP with sub-second price updates.</p>
            <p>Resolution via Chainlink oracle price feeds.</p>
          </div>
          <div className="space-y-1">
            <p className="text-white font-semibold text-sm">🧠 Algorithm</p>
            <p>9-signal analysis: price delta, short/med momentum, RSI, 1m/5m candle trends, volume, VWAP, time decay.</p>
            <p>Enters 2.5-4.5 min into each window when direction is clearest.</p>
          </div>
          <div className="space-y-1">
            <p className="text-white font-semibold text-sm">🛡️ Risk Management</p>
            <p>Auto-stop after {ALGO_CONSTANTS.MAX_CONSECUTIVE_LOSSES} consecutive losses. Daily trade cap of {ALGO_CONSTANTS.MAX_DAILY_TRADES}.</p>
            <p>No exit needed — markets auto-resolve in 5 minutes.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Stat card component
function StatCard({ label, value, icon, color = 'orange' }) {
  const colors = {
    orange: 'from-orange-500/10 to-transparent border-orange-500/20',
    emerald: 'from-emerald-500/10 to-transparent border-emerald-500/20',
    red: 'from-red-500/10 to-transparent border-red-500/20'
  };
  
  return (
    <div className={`bg-gradient-to-br ${colors[color] || colors.orange} border rounded-xl p-4`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-dark-400 font-medium uppercase tracking-wider">{label}</span>
        {icon}
      </div>
      <p className="text-xl font-bold text-white">{value}</p>
    </div>
  );
}

const ALGO_CONSTANTS = {
  MAX_CONSECUTIVE_LOSSES: 5,
  MAX_DAILY_TRADES: 100
};
